import { useState, useEffect } from "react";
import axios from "axios";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import { API, LOGO_IMAGE } from "./constants";

// Sector-specific copy for every B2B payment-success page (spec: Louise, Feb 2026).
// Family/personal purchases fall through to the untouched original copy below.
const SECTOR = {
  hotel:   { label: "hotel",        Label: "Hotel",        catTile: "Hotels",        codeThing: "venue" },
  school:  { label: "school",       Label: "School",       catTile: "Schools",       codeThing: "school, classroom or provision" },
  charity: { label: "organisation", Label: "Organisation", catTile: "Organisations", codeThing: "service, venue or provision" },
};

const PaymentSuccess = ({ onBack }) => {
  const [status, setStatus] = useState("checking");
  const [error, setError] = useState("");
  const [b2b, setB2b] = useState(null); // {kind, sector, hotel_name, manager_email, tier_name, num_codes, monthly_credits, credits_added}

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus("error"); setError("No payment session found."); return; }

    let cancelled = false;

    // Step 1: try the B2B endpoint first. If it 200s, this is a hotel/school/charity purchase.
    axios.get(`${API}/hotel/session-info?session_id=${encodeURIComponent(sessionId)}`)
      .then((r) => { if (!cancelled) { setB2b(r.data); setStatus("success"); } })
      .catch(() => {
        // Not a B2B session — poll the family success endpoint (unchanged behaviour).
        let attempts = 0;
        const maxAttempts = 8;
        const poll = async () => {
          if (cancelled) return;
          try {
            const res = await axios.get(`${API}/checkout/status/${sessionId}`);
            if (res.data.payment_status === "paid") { setStatus("success"); return; }
            if (res.data.status === "expired") { setStatus("error"); setError("Payment session expired."); return; }
            attempts++;
            if (attempts < maxAttempts) setTimeout(poll, 2500);
            else setStatus("success");
          } catch {
            attempts++;
            if (attempts < maxAttempts) setTimeout(poll, 2500);
            else { setStatus("error"); setError("Could not verify payment. Check your email for your code."); }
          }
        };
        poll();
      });

    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex flex-col items-center justify-center p-6">
      <div className="max-w-md w-full text-center">
        <a href="https://www.poodlesandfriends.uk/" target="_blank" rel="noopener noreferrer" className="inline-block mb-8">
          <img src={LOGO_IMAGE} alt="Poodles and Friends" className="h-20 w-auto object-contain mx-auto" />
        </a>

        {status === "checking" && (
          <>
            <Loader2 className="w-16 h-16 text-[#C9B896] mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-[#4A4A4A] mb-3">Processing your payment...</h1>
            <p className="text-[#6B6B6B]">Just a moment while we confirm everything.</p>
          </>
        )}

        {status === "success" && b2b && <B2bSuccess b2b={b2b} onBack={onBack} />}

        {status === "success" && !b2b && <FamilySuccess onBack={onBack} />}

        {status === "error" && (
          <>
            <XCircle className="w-20 h-20 text-[#A55B64] mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-[#4A4A4A] mb-3">Something went wrong</h1>
            <p className="text-[#6B6B6B] mb-6">{error}</p>
            <button onClick={onBack} data-testid="error-back-button"
              className="px-8 py-3 bg-[#F3F0E6] text-[#6B6B6B] rounded-full font-medium hover:bg-[#E8E5DC] transition-all">
              Back to Poodle Talk
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// -- Family / personal (UNCHANGED copy per spec) ---------------------------
function FamilySuccess({ onBack }) {
  return (
    <>
      <CheckCircle className="w-20 h-20 text-[#5A8A5A] mx-auto mb-6" />
      <h1 className="text-2xl sm:text-3xl font-bold text-[#4A4A4A] mb-3">Payment Successful!</h1>
      <p className="text-[#6B6B6B] text-lg mb-6">Your chat credit code has been sent to your email.</p>
      <div className="bg-[#F9F6EE] rounded-2xl p-5 border border-[#E8E5DC] mb-6 text-left">
        <h3 className="font-semibold text-sm text-[#4A4A4A] mb-2">How to use your code:</h3>
        <ol className="text-sm text-[#6B6B6B] space-y-1 list-decimal list-inside">
          <li>Check your email for the code</li>
          <li>Open Poodle Talk</li>
          <li>Go to Parents Corner</li>
          <li>Tap "Enter Code"</li>
          <li>Paste your code — credits added instantly!</li>
        </ol>
      </div>
      <button onClick={onBack} data-testid="success-back-to-chat"
        className="px-8 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] transition-all hover:scale-105">
        Back to Poodle Talk
      </button>
    </>
  );
}

// -- B2B / organisation purchases (Louise's Feb 2026 spec) ------------------
function B2bSuccess({ b2b, onBack }) {
  const sec = SECTOR[b2b.sector] || SECTOR.hotel;
  const isAddon = b2b.kind === "hotel_addon";
  const email = b2b.manager_email || "your registered email";
  return (
    <>
      <CheckCircle className="w-20 h-20 text-[#5A8A5A] mx-auto mb-6" />
      <h1 className="text-2xl sm:text-3xl font-bold text-[#4A4A4A] mb-3" data-testid="hotel-success-title">
        Payment Successful!
      </h1>

      {isAddon ? (
        <p className="text-[#6B6B6B] text-lg mb-6" data-testid="hotel-success-message">
          Your additional {(b2b.credits_added || 0).toLocaleString()} credits have been added to your shared {sec.label} credit balance.
        </p>
      ) : (
        <p className="text-[#6B6B6B] text-lg mb-6" data-testid="hotel-success-message">
          Your {sec.label} membership is now active.<br />
          Your {b2b.num_codes || 1} {sec.label} activation code{(b2b.num_codes || 1) > 1 ? "s" : ""} and setup instructions have been sent to <span className="font-semibold text-[#4A4A4A]">{email}</span>.
        </p>
      )}

      {!isAddon && (
        <div className="bg-[#F9F6EE] rounded-2xl p-5 border border-[#E8E5DC] mb-6 text-left" data-testid="hotel-success-instructions">
          <h3 className="font-semibold text-sm text-[#4A4A4A] mb-2">Instructions:</h3>
          <ol className="text-sm text-[#6B6B6B] space-y-1 list-decimal list-inside">
            <li>Check your email for the {sec.label} activation code{(b2b.num_codes || 1) > 1 ? "s" : ""}.</li>
            <li>Open Poodle Talk.</li>
            <li>Select "Organisation Sign In."</li>
            <li>Choose "{sec.catTile}."</li>
            <li>Select "Enter activation code."</li>
            <li>Enter the appropriate {sec.codeThing} code and give the device a name.</li>
            <li>Repeat on the other devices as required.</li>
          </ol>
        </div>
      )}

      <p className="text-xs text-[#6B6B6B] mb-6" data-testid="hotel-success-footer">
        All activated {sec.label} devices share the same central {sec.label} credit balance.
      </p>

      <button onClick={onBack} data-testid="hotel-success-open-app"
        className="px-8 py-3 bg-[#E8D4A8] text-[#5A4A32] rounded-full font-semibold hover:bg-[#D9C599] transition-all hover:scale-105">
        Open Poodle Talk
      </button>
    </>
  );
}

export default PaymentSuccess;
